import { RemoteConfigVariables } from 'fitbeat.models';
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator, BackHandler, Dimensions,
  SafeAreaView, StyleSheet, TouchableOpacity, View,
} from 'react-native';
import Product from 'react-native-iaphub/src/models/product'; // todo: need to not import from their internal models
import { connect } from 'react-redux';
import FitillionLogo from '../assets/images/fitllion-horizontal-logo-gym-smart.svg';
import headerImage from '../assets/images/header-images/home-workout-knee-to-elbow.jpg';
import { Button, Icon } from '../components';
import { IAppStore } from '../HomeWorkouts';
import { GoogleAnalytics } from '../services/google-analytics';
import { InAppPurchaseService } from '../services/iap';
import Theme from '../theme';
import { TrackingImageTile } from '../Tracking/components/TrackingImageTile';
import { FirebaseConfigText } from '../ui';
import { AppStateFocusedEvents, FormError } from '../ui';
import { defaultScale, LogoutHelper, scaleImageAspectRatio, UserDataConsumer } from '../utils';
import { Logger } from '../utils/Logger';
import { buyIapProduct } from './actions';
import { IAPCloseScreenModal } from './components';

interface IProps {
  navigation?: any;
  isLoading: boolean;
  error?: string;
  product: Product | null;
  activeProduct: Product | null;
  forcePaymentOrLogout: boolean;
  t(key: string, options?: any): string;
  onJoinSuccess(): void;
  buyIapProduct(onSuccess: () => void): void;
  onAndroidBackButton?(): boolean | null | undefined;
}

interface IState {
  error: string | null;
  closeModalVisible: boolean;
}

class InAppPurchaseScreen extends PureComponent<IProps, IState> {
  private googleAnalytics = new GoogleAnalytics();

  constructor(props: any) {
    super(props);

    this.state = {
      error: null,
      closeModalVisible: false,
    };
  }

  public addAndroidBackHandler = () => {
    const { onAndroidBackButton } = this.props;
    const backHandler = onAndroidBackButton || this.androidBackHandler;
    BackHandler.addEventListener('hardwareBackPress', backHandler);
  }

  public removeAndroidBackHandler = () => {
    const { onAndroidBackButton } = this.props;
    const backHandler = onAndroidBackButton || this.androidBackHandler;
    BackHandler.removeEventListener('hardwareBackPress', backHandler);
  }

  public androidBackHandler = (): boolean => {
    this.toggleConfirmCloseModal()
      .catch((e) => Logger.notify(e));
    return true;
  }

  public join = () => {
    const { onJoinSuccess } = this.props;
    this.props.buyIapProduct(onJoinSuccess);
    this.googleAnalytics.logEventWithParamaters('sign_up_join_trial_iap');
  }

  public toggleConfirmCloseModal = async () => {
    const closeModalVisible = !this.state.closeModalVisible;
    if (closeModalVisible) {
      this.googleAnalytics.logEventWithParamaters('sign_up_request_close_join_trial_iap');
    }
    await this.setState({ closeModalVisible });
  }

  public closeModalAndJoin = () => {
    this.setState({ closeModalVisible: false });
    this.join();
    this.googleAnalytics.logEventWithParamaters('sign_up_confirm_close_join_trial_iap');
  }

  public logout = async () => {
    await LogoutHelper();
    this.googleAnalytics.logEventWithParamaters('sign_up_cancel_close_join_trial_iap');
  }

  public render() {
    const {
      t, isLoading, product, error,
      navigation, activeProduct, forcePaymentOrLogout,
    } = this.props;
    const { closeModalVisible } = this.state;

    return (
      <View style={styles.container}>
        <AppStateFocusedEvents
          navigation={navigation}
          enableBackgroundFocusEvent={false}
          onScreenFocus={this.addAndroidBackHandler}
          onScreenUnfocus={this.removeAndroidBackHandler}
        />
        {forcePaymentOrLogout &&
          <TouchableOpacity
            style={styles.closeButton}
            onPress={this.toggleConfirmCloseModal}>
            <Icon
              name='md-close'
              color={Theme.WHITE}
              size={40}
            />
          </TouchableOpacity>
        }
        <TrackingImageTile
          imageSource={headerImage}
          containerStyle={styles.headerImageContainer}
          actions={<></>}
        />
        <View style={styles.contentContainer}>
          {product &&
            <>
              <FitillionLogo height={defaultScale(50)} />
              <View>
                <FirebaseConfigText
                  remoteConfigKey={RemoteConfigVariables.paywall_payment_text}
                  style={styles.header}
                  stringTemplateValues={{ price: product.localizedPrice }}
                />
                <View style={styles.productDescriptionContainer}>
                  <FirebaseConfigText style={styles.productDescription}
                                      remoteConfigKey={RemoteConfigVariables.in_app_purchase_screen_product_description}
                  />
                </View>
                <FirebaseConfigText style={[styles.productDescription, { paddingTop: defaultScale(15) }]}
                                    remoteConfigKey={RemoteConfigVariables.in_app_purchase_screen_benefit} />
              </View>
            </>
          }
        </View>
        <SafeAreaView style={styles.footer}>
          {isLoading ?
            <ActivityIndicator animating color={Theme.PRIMARY} />
            :
            <>
              {error &&
                <FormError
                  color={Theme.GREY_DARK}
                  error={new Error(error)}
                />
              }
              {!activeProduct &&
                <Button
                  title={t('joinNow')}
                  fontColor={Theme.WHITE}
                  onPress={this.join}
                />
              }
            </>
          }
        </SafeAreaView>
        {closeModalVisible &&
          <IAPCloseScreenModal
            t={t}
            joinNow={this.closeModalAndJoin}
            logout={this.logout}
          />
        }
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 0,
    backgroundColor: Theme.WHITE,
  },
  header: {
    ...Theme.textStyle.heading,
    textAlign: 'center',
    marginTop: defaultScale(10),
  },
  productDescriptionContainer: {
    paddingTop: defaultScale(10),
    paddingBottom: defaultScale(15),
    borderBottomWidth: 0.5,
    borderBottomColor: Theme.GREY,
  },
  productDescription: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'space-evenly',
    margin: defaultScale(15),
  },
  footer: {
    marginHorizontal: defaultScale(15),
    marginBottom: defaultScale(15),
  },
  closeButton: {
    position: 'absolute',
    zIndex: 1,
    right: 40,
    top: 40,
  },
  headerImageContainer: {
    height: undefined,
    width: '100%',
    aspectRatio: scaleImageAspectRatio(1),
  },
});

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  return {
    error: state.auth.signup.iap.error,
    isLoading: state.auth.signup.iap.isLoading,
    product: state.auth.signup.iap.product,
    activeProduct: state.auth.signup.iap.activeProduct,
  };
};

const mapDispatchToProps = (dispatch): Partial<IProps> => ({
  buyIapProduct: (onSuccess) => dispatch(buyIapProduct(InAppPurchaseService, onSuccess)),
});

const InAppPurchaseRedux = connect(mapStateToProps, mapDispatchToProps)(InAppPurchaseScreen);
export const InAppPurchase = translate(['auth', 'formFields'], { wait: true })(
  UserDataConsumer(InAppPurchaseRedux),
);
